import {
  MultiOptionConfirmModal,
  type MultiOptionConfirmModalOption,
} from '@resistapp/client/components/modals/multi-option-confirmation-modal';
import { fetchOrThrow } from '@resistapp/client/hooks/api';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { OrganisationProjectAccessOptionsResponse } from '@resistapp/common/api-types';
import { OrganisationSummary } from '@resistapp/common/types';
import { useState } from 'react';
import { ResistomapSelect } from '../forms/resistomap-select';

interface Option {
  label: string;
  value: number;
}

function isOrganisationProjectAccessOptionsResponse(data: unknown): data is OrganisationProjectAccessOptionsResponse {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const candidate = data as OrganisationProjectAccessOptionsResponse & {
    samplingPointOption?: { totalSamples?: unknown; outsideProjectSamples?: unknown };
  };
  if (!('samplingPointOption' in candidate)) {
    return false;
  }
  const samplingPointOption = candidate.samplingPointOption as {
    totalSamples?: unknown;
    outsideProjectSamples?: unknown;
  };
  return (
    typeof candidate.projectSampleCount === 'number' &&
    typeof candidate.projectSamplingPointCount === 'number' &&
    typeof candidate.currentVisibleProjectSampleCount === 'number' &&
    typeof candidate.projectSamplesWithoutSamplingPointCount === 'number' &&
    typeof samplingPointOption.totalSamples === 'number' &&
    typeof samplingPointOption.outsideProjectSamples === 'number'
  );
}

export function AddOrganisationAccessDropdown({
  organisations,
  projectId,
}: {
  organisations: OrganisationSummary[];
  projectId: number;
}) {
  const options: Option[] = organisations.map(org => ({ label: org.name, value: org.id }));
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [accessOptions, setAccessOptions] = useState<OrganisationProjectAccessOptionsResponse | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const showToast = useToastWithoutDebounce();

  const reset = () => {
    setSelectedOption(null);
    setAccessOptions(null);
    setIsModalOpen(false);
  };

  const handleSelect = async (option: Option | null) => {
    if (!option) return;
    try {
      const response = await fetchOrThrow<undefined, OrganisationProjectAccessOptionsResponse>(
        'GET',
        `/organisation/${option.value}/project/${projectId}/access-options`,
      );
      const status = response.status ?? 0;
      const data = response.data;
      if (status < 200 || status > 204 || !isOrganisationProjectAccessOptionsResponse(data)) {
        const message = response.statusText || 'Failed to load access options';
        showToast({ title: 'Failed to load access options', description: message, status: 'error' });
        return;
      }
      setSelectedOption(option);
      setAccessOptions(data);
      setIsModalOpen(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load access options';
      showToast({ title: 'Failed to load access options', description: message, status: 'error' });
    }
  };

  const applyAccess = async (scope: 'project' | 'samples' | 'sampling-points') => {
    if (!selectedOption) return;
    const response = await fetchOrThrow<undefined, boolean>(
      'POST',
      `/organisation/${selectedOption.value}/project/${projectId}?scope=${scope}`,
    );
    const status = response.status ?? 0;
    if (status < 200 || status > 204) {
      const message = response.statusText || 'Failed to update access';
      showToast({ title: 'Failed to grant access', description: message, status: 'error' });
      throw new Error(message);
    }
    window.location.reload();
  };

  return (
    <div style={{ marginTop: '8px' }}>
      <span style={{ fontWeight: 701 }}>Grant access to:</span>
      <ResistomapSelect<(typeof options)[number], false>
        placeholder="-"
        chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
        options={options}
        value={selectedOption}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onChange={handleSelect}
      />
      {selectedOption && accessOptions ? (
        <MultiOptionConfirmModal
          isOpen={isModalOpen}
          onClose={reset}
          title={`Grant ${selectedOption.label} access`}
          message={`Select how much of project #${projectId} to share with ${selectedOption.label}.`}
          options={((): MultiOptionConfirmModalOption[] => {
            const modalOptions: MultiOptionConfirmModalOption[] = [
              {
                key: 'samples',
                label: `Project + ${accessOptions.projectSampleCount} samples`,
                colorScheme: 'green',
                description: 'Grant explicit sample-level access to every sample in this project.',
                onSelect: async () => {
                  await applyAccess('samples');
                },
              },
              {
                key: 'sampling-points',
                label: `Project + sampling points`,
                colorScheme: 'purple',
                description: `Grant sampling point access (${accessOptions.projectSamplingPointCount} points) covering ${accessOptions.samplingPointOption.totalSamples} samples (${accessOptions.samplingPointOption.outsideProjectSamples} outside this project). Future samples at these points will also be visible.`,
                onSelect: async () => {
                  await applyAccess('sampling-points');
                },
              },
            ];

            if (accessOptions.currentVisibleProjectSampleCount > 0) {
              modalOptions.unshift({
                key: 'project',
                label: 'Project shell only',
                variant: 'outline',
                colorScheme: 'blue',
                description: `${selectedOption.label} already sees ${accessOptions.currentVisibleProjectSampleCount} project samples via existing sampling permissions.`,
                onSelect: async () => {
                  await applyAccess('project');
                },
              });
            }

            return modalOptions;
          })()}
        />
      ) : null}
    </div>
  );
}
